import { ft } from '../core/units.js';
import { segmentClear } from '../core/collision.js';
import { behindBody } from '../core/camera.js';
import { CLOTHING } from './clothing.js';
import { EVENT } from './events.js';
import { FIRST_BEO } from '../data/beos.js';
import { OPENING } from '../data/opening.js';

/**
 * A new starter's first afternoon: setting it up, and running it frame by
 * frame until it is over. It owns the one piece of state the afternoon has,
 * and it is null for any week that did not start at the hiring screen.
 *
 * `activeBeo` and `markDay` are asked for rather than held: the board is built
 * before this is, and the day's checkpoint belongs to whoever saves.
 */
export function createOpening({
  clock,
  clothing,
  player,
  rig,
  npcs,
  perception,
  bus,
  level,
  autosave,
  applyShortfalls,
  pocket,
  activeBeo,
  markDay,
}) {
  let opening = null;

  /** The first room, as that day's whole board. A fresh copy each time it is asked for. */
  const firstBoard = (day) => [
    { ...FIRST_BEO, day, shortfalls: FIRST_BEO.shortfalls.map((entry) => ({ ...entry })) },
  ];

  /**
   * The first afternoon, set up and left alone. The numbers are in
   * `data/opening.js`.
   *
   * A new starter comes in the way staff come in: in their own clothes, from the
   * staff lot, a few minutes after one, with the shift opening at one and
   * starting at two. Nothing is said to them. What there is instead is where
   * everybody already stands — a valet at the dock doors facing the way they
   * will come in, a locker in the break room, a clock that will not take a
   * punch from somebody in a hoodie, and a supervisor at the mouth of the
   * alcove — and one room on the board.
   *
   * Only here. Every other reset, including every one the scripts make, is the
   * old week: in blacks, in the break room, pocket full.
   */
  function beginOpening() {
    opening = { day: clock.day, serg: 'waiting', greeted: false, incident: 'pending' };
    clock.load({ day: clock.day, minutes: OPENING.minutes, scale: clock.scale ?? 1 });
    clothing.wear(CLOTHING.CIVILIAN);
    pocket.empty();
    const [x, z] = OPENING.arrive.at;
    const [fx, fz] = OPENING.arrive.faces;
    const yaw = Math.atan2(fx - x, fz - z);
    player.teleport({ x: ft(x), y: 0, z: ft(z) });
    player.yaw = yaw;
    rig.yaw = behindBody(yaw);
    applyShortfalls();
    markDay();
    autosave.touch();
  }

  /** Whether a point is in front of the player, near enough, with nothing in the way. */
  function inEyeline(point) {
    const dx = point.x - player.position.x;
    const dz = point.z - player.position.z;
    const gap = Math.hypot(dx, dz);
    if (gap > ft(OPENING.incident.reach) || gap < 1e-3) return false;
    const forward = rig.forward;
    const length = Math.hypot(forward.x, forward.z) || 1;
    if ((dx * forward.x + dz * forward.z) / (gap * length) < OPENING.incident.eyeline) return false;
    const eye = player.eye;
    return segmentClear({ x: eye.x, y: eye.y, z: eye.z }, { x: point.x, y: 1.2, z: point.z }, level.world);
  }

  /** Every frame of the first afternoon: Serg at the clock, and the one incident. */
  function runOpening(status) {
    if (!opening || clock.day !== opening.day) return;
    // Whoever's row has a first-day post. There is one, and it is Serg.
    const serg = npcs.find((npc) => npc.data.firstDay?.post);
    const first = serg?.data.firstDay;

    if (serg && first && opening.serg === 'waiting') {
      if (serg.present && !serg.aboard) {
        const yaw = Math.atan2(first.faces[0] - first.post[0], first.faces[1] - first.post[1]);
        serg.board(() => ({ x: ft(first.post[0]), y: 0, z: ft(first.post[1]), yaw }));
      }
      const near = Math.hypot(serg.position.x - player.position.x, serg.position.z - player.position.z) < ft(16);
      if (serg.aboard && !opening.greeted && near && perception.sees(serg.data.id)) {
        serg.say(first.says, 8);
        opening.greeted = true;
        autosave.touch();
      }
      if (activeBeo()?.id === FIRST_BEO.id) {
        serg.alight(first.back.map(([bx, bz]) => ({ x: ft(bx), z: ft(bz) })));
        opening.serg = 'gone';
        autosave.touch();
      }
    }

    // Who does it and who sees it are on their own rows.
    const actor = npcs.find((npc) => npc.data.firstDay?.does);
    const witness = npcs.find((npc) => actor && npc.data.firstDay?.sees === actor.data.id);
    const act = actor?.data.firstDay.does;
    const both = actor?.present && witness?.present && !actor.aboard && !witness.aboard;
    /*
     * In two steps, with the vocabulary people already have. Once the two of
     * them are in front of the player, Ryan goes over to where Lil D is — a
     * detour, the same as anybody going to look at something — and he glances
     * up as she gets there. Then, if the player is still looking, she does it.
     * Two routes crossing at arm's length in the player's eyeline is a thing
     * that happens about once a week by itself, and this is the first shift.
     */
    if (opening.incident === 'pending' && status.onTheClock && both && inEyeline(actor.position) && inEyeline(witness.position)) {
      const facing = { x: Math.sin(witness.yaw), z: Math.cos(witness.yaw) };
      actor.goAndLook({ x: witness.position.x + facing.x * ft(4), z: witness.position.z + facing.z * ft(4) });
      opening.incident = 'approaching';
      opening.approachingFor = 0;
    }
    if (opening.incident === 'approaching') {
      opening.approachingFor = (opening.approachingFor ?? 0) + 1 / 60;
      const close =
        both && Math.hypot(witness.position.x - actor.position.x, witness.position.z - actor.position.z) <= ft(OPENING.incident.close);
      if (close && !witness.holding) witness.hold(3, { at: actor.position });
      if (!both || !status.onTheClock || opening.approachingFor > 20) {
        // It did not come together. Back to waiting for the next chance.
        opening.incident = 'pending';
      } else if (
        close &&
        perception.looking(witness.data.id, actor.position) &&
        inEyeline(actor.position) && inEyeline(witness.position)
      ) {
        actor.say(act.says, 4);
        bus.emit(EVENT.TAKE, {
          actor: actor.data.id,
          kind: act.kind,
          position: { x: actor.position.x, y: 0, z: actor.position.z },
        });
        opening.incident = 'fired';
        autosave.touch();
      }
    }
  }

  return {
    begin: beginOpening,
    run: runOpening,
    /** The afternoon's state, or null. Saved as it is. */
    get state() {
      return opening;
    },
    /** Day one's board when the afternoon is running, or null for the ordinary board. */
    boardFor(day) {
      return opening && day === opening.day ? firstBoard(day) : null;
    },
    load(saved) {
      opening = saved ?? null;
    },
    clear() {
      opening = null;
    },
  };
}
